// src/pages/AccountPage.js
import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useApp } from '../context/AppContext';
import { usersAPI } from '../services/api';
import { Toggle } from '../components/ui';

const AccountPage = ({ onNav }) => {
  // logout now comes from AppContext — works for both Auth0 + external SSO
  const { currentUser, readingTheme, setReadingTheme, fontSize, setFontSize, downloads, getToken, logout } = useApp();
  const [tab, setTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [emailNotif, setEmailNotif] = useState(currentUser?.prefs?.emailNotif !== false);
  const [autoDown, setAutoDown] = useState(currentUser?.prefs?.autoDownload === true);

  // Update when user loads
  React.useEffect(() => {
    if (currentUser?.prefs) {
      setEmailNotif(currentUser.prefs.emailNotif !== false);
      setAutoDown(currentUser.prefs.autoDownload === true);
    }
  }, [currentUser?.prefs]);

  const handleSaveNotifPrefs = async () => {
    try {
      const token = await getToken();
      await usersAPI.updateMe(token, { prefs: { emailNotif, autoDownload: autoDown } });
      showToast('Preferences saved ✓');
    } catch { showToast('Failed to save'); }
  };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2000); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      await usersAPI.updateMe(token, { readingTheme, fontSize });
      showToast('Settings saved ✓');
    } catch { showToast('Failed to save'); }
    finally { setSaving(false); }
  };

  const initials = (currentUser?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase();

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-20 animate-fade-in" style={{ fontFamily: 'Nunito,system-ui,sans-serif' }}>
      <h1 className="font-display font-bold mb-5" style={{ fontFamily: 'Playfair Display,serif', fontSize: 24, color: 'var(--ink)' }}>
        Account <em style={{ color: 'var(--rose)' }}>Settings</em>
      </h1>

      {/* Profile card */}
      <div className="bg-white rounded-xl border p-5 flex items-center gap-4 mb-5" style={{ borderColor: 'var(--border)' }}>
        <div className="rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
          style={{ width: 56, height: 56, background: 'var(--rose)', fontSize: 20, fontFamily: 'Nunito,sans-serif' }}>{initials}</div>
        <div className="flex-1">
          <div className="font-display font-bold" style={{ fontFamily: 'Playfair Display,serif', fontSize: 18, color: 'var(--ink)' }}>
            {currentUser?.name || 'User'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif', marginTop: 2 }}>{currentUser?.email || ''}</div>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <span className="font-bold rounded-full px-3 py-1 border"
              style={{ fontSize: 10, color: 'var(--rose)', background: 'var(--rose-pale)', borderColor: 'var(--rose-lt)', fontFamily: 'Nunito,sans-serif' }}>
              ◉ {currentUser?.plan || 'Free'}
            </span>
            {(currentUser?.packages || currentUser?.allowedPackages || []).map(pkg => (
              <span key={pkg} className="font-bold rounded-full px-2.5 py-0.5 border"
                style={{ fontSize: 10, color: '#1e40af', background: '#eff6ff', borderColor: '#bfdbfe', fontFamily: 'Nunito,sans-serif' }}>
                📦 {pkg}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto no-scrollbar border-b mb-5" style={{ borderColor: 'var(--border)' }}>
        {['profile', 'subscription', 'reading', 'notifications'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className="px-4 py-2.5 font-bold whitespace-nowrap border-b-2 capitalize transition-all"
            style={{
              fontSize: 11, fontFamily: 'Nunito,sans-serif',
              color: tab === t ? 'var(--rose)' : 'var(--ink-lt)',
              borderColor: tab === t ? 'var(--rose)' : 'transparent',
            }}>
            {t}
          </button>
        ))}
      </div>

      {/* Profile */}
      {tab === 'profile' && (
        <div className="space-y-4 animate-fade-in">
          <div className="bg-white rounded-xl border p-5" style={{ borderColor: 'var(--border)' }}>
            <div className="font-bold uppercase mb-4" style={{ fontSize: 10, color: 'var(--ink-lt)', letterSpacing: 2, fontFamily: 'Nunito,sans-serif' }}>Personal Information</div>
            {[['Name', currentUser?.name || '—'], ['Email', currentUser?.email || '—'], ['Plan', currentUser?.plan || 'Free']].map(([label, value]) => (
              <div key={label} className="flex justify-between items-center py-3 border-b last:border-0" style={{ borderColor: 'var(--border-lt)' }}>
                <span style={{ fontSize: 13, color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif' }}>{label}</span>
                <span className="font-bold" style={{ fontSize: 13, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif' }}>{value}</span>
              </div>
            ))}
          </div>
          <button onClick={() => logout()}
            className="w-full py-3 rounded-xl font-bold border cursor-pointer transition-colors hover:bg-rose-pale"
            style={{ fontSize: 13, color: 'var(--rose)', borderColor: 'rgba(201,123,119,.3)', background: 'transparent', fontFamily: 'Nunito,sans-serif' }}>
            Sign Out
          </button>
        </div>
      )}

      {/* Subscription */}
      {tab === 'subscription' && (
        <div className="space-y-4 animate-fade-in">
          <div className="bg-white rounded-xl border p-5" style={{ borderColor: 'var(--border)', borderLeft: '3px solid var(--rose)' }}>
            <div className="font-bold uppercase mb-2" style={{ fontSize: 10, color: 'var(--rose)', letterSpacing: 2, fontFamily: 'Nunito,sans-serif' }}>Current Plan</div>
            <div className="font-display font-bold mb-3" style={{ fontFamily: 'Playfair Display,serif', fontSize: 20, color: 'var(--ink)' }}>
              {currentUser?.plan || 'Free'}
            </div>
            {(currentUser?.packages || currentUser?.allowedPackages || []).length > 0 && (
              <div className="mb-4">
                <div className="font-bold uppercase mb-2" style={{ fontSize: 10, color: 'var(--ink-lt)', letterSpacing: 1.5, fontFamily: 'Nunito,sans-serif' }}>
                  Active Allowed Packages
                </div>
                <div className="flex flex-wrap gap-2">
                  {(currentUser?.packages || currentUser?.allowedPackages || []).map(pkg => (
                    <span key={pkg} className="font-bold rounded-lg px-3 py-1 border flex items-center gap-1.5"
                      style={{ fontSize: 11, color: '#1e40af', background: '#eff6ff', borderColor: '#bfdbfe', fontFamily: 'Nunito,sans-serif' }}>
                      <span>✓</span> {pkg}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <button onClick={() => onNav('pricing')}
              className="w-full py-2.5 text-white rounded-xl font-bold border-0 cursor-pointer hover:opacity-90 transition-opacity"
              style={{ background: 'var(--rose)', fontSize: 12, fontFamily: 'Nunito,sans-serif' }}>
              Upgrade Plan
            </button>
          </div>
          {/* <div className="bg-white rounded-xl border p-5" style={{ borderColor: 'var(--border)' }}>
            <div className="font-bold uppercase mb-3" style={{ fontSize: 10, color: 'var(--ink-lt)', letterSpacing: 2, fontFamily: 'Nunito,sans-serif' }}>
              Downloads ({downloads.length})
            </div>
            {downloads.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif' }}>No downloads yet</p>
            ) : downloads.map(id => (
              <div key={id} className="flex justify-between py-3 border-b last:border-0" style={{ borderColor: 'var(--border-lt)' }}>
                <span style={{ fontSize: 13, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif' }}>Book #{id}</span>
              </div>
            ))}
          </div> */}
        </div>
      )}

      {/* Reading */}
      {tab === 'reading' && (
        <div className="space-y-4 animate-fade-in">
          <div className="bg-white rounded-xl border p-5" style={{ borderColor: 'var(--border)' }}>
            <div className="font-bold uppercase mb-4" style={{ fontSize: 10, color: 'var(--ink-lt)', letterSpacing: 2, fontFamily: 'Nunito,sans-serif' }}>Reading Theme</div>
            <div className="grid grid-cols-3 gap-3">
              {[['light', '☀️', 'Light'], ['sepia', '🌤️', 'Sepia'], ['dark', '🌙', 'Dark']].map(([key, icon, label]) => (
                <button key={key} onClick={() => setReadingTheme(key)}
                  className="py-3 rounded-xl border font-bold cursor-pointer transition-all"
                  style={{
                    fontSize: 11, fontFamily: 'Nunito,sans-serif',
                    borderColor: readingTheme === key ? 'var(--rose)' : 'var(--border)',
                    background: readingTheme === key ? 'var(--rose-pale)' : 'transparent',
                    color: readingTheme === key ? 'var(--rose)' : 'var(--ink-mid)',
                  }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>{label}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl border p-5" style={{ borderColor: 'var(--border)' }}>
            <div className="font-bold uppercase mb-4" style={{ fontSize: 10, color: 'var(--ink-lt)', letterSpacing: 2, fontFamily: 'Nunito,sans-serif' }}>Font Size</div>
            <div className="flex justify-between mb-2" style={{ fontSize: 12, color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif' }}>
              <span>Font Size</span><span className="font-bold" style={{ color: 'var(--ink)' }}>{fontSize}px</span>
            </div>
            <input type="range" min={12} max={22} value={fontSize} onChange={e => setFontSize(Number(e.target.value))}
              className="w-full cursor-pointer" style={{ accentColor: 'var(--rose)' }} />
            <div className="flex justify-between mt-1" style={{ fontSize: 9, color: 'var(--ink-faint)', fontFamily: 'Nunito,sans-serif' }}>
              <span>Small (12px)</span><span>Large (22px)</span>
            </div>
          </div>
          <button onClick={handleSave} disabled={saving}
            className="w-full py-3 text-white rounded-xl font-bold border-0 cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{ background: 'var(--rose)', fontSize: 13, fontFamily: 'Nunito,sans-serif' }}>
            {saving ? 'Saving…' : 'Save Preferences'}
          </button>
        </div>
      )}

      {/* Notifications */}
      {tab === 'notifications' && (
        <div className="animate-fade-in">
          <div className="bg-white rounded-xl border p-5" style={{ borderColor: 'var(--border)' }}>
            <Toggle value={emailNotif} onChange={setEmailNotif} label="New edition alerts" description="Email when new or updated editions are published" />
            <div className="h-px" style={{ background: 'var(--border-lt)' }} />
            <Toggle value={autoDown} onChange={setAutoDown} label="Auto-download new editions" description="Downloads when on Wi-Fi only" />
            <div className="h-px" style={{ background: 'var(--border-lt)' }} />
            <Toggle value={true} onChange={() => { }} label="LexAssist updates" description="New AI features and improvements" />
          </div>
          <button onClick={handleSaveNotifPrefs}
            className="w-full py-3 text-white rounded-xl font-bold border-0 cursor-pointer mt-4"
            style={{ background: 'var(--rose)', fontSize: 13, fontFamily: 'Nunito,sans-serif' }}>
            Save Preferences
          </button>
        </div>
      )}

      {/* Legal */}
      <div className="mt-8 text-center space-y-1">
        {['Privacy Policy (DPDP Act 2023)', 'Terms of Use', 'Data Erasure Request'].map(item => (
          <button key={item} className="block w-full py-0.5 transition-colors hover:text-rose"
            style={{ fontSize: 11, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif', background: 'none', border: 'none', cursor: 'pointer' }}>{item}</button>
        ))}
        <div style={{ fontSize: 10, color: 'var(--ink-faint)', fontFamily: 'Nunito,sans-serif', marginTop: 8 }}>
          AIR Reader v1.0.0 · All India Reporter (P) Ltd · Nagpur
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
          <div className="px-5 py-3 rounded-xl shadow-lg text-white text-xs font-bold"
            style={{ background: 'var(--sage)', fontFamily: 'Nunito,sans-serif' }}>{toast}</div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;